import clsx from 'clsx'
import parse from 'autosuggest-highlight/parse'
import match from 'autosuggest-highlight/match'
import { Typography, IconButton } from '@mui/material'
import { ESuggestionType, type SuggestionItem } from '@obeta/data/lib/hooks/useSearchDropdownData'
import { CategoryIcon } from '../../../category-icon'
import { SearchIcon, ClockIcon, LinkExternalIcon } from '../../../icons'
import styles from './AutocompleteOption.module.scss'

export type AutocompleteOptionProps = {
  optionProps: React.HTMLAttributes<HTMLLIElement>
  option: SuggestionItem
  inputValue: string
  selected: boolean
  setSearchInputValue: React.Dispatch<React.SetStateAction<SuggestionItem>>
}

export const AutocompleteOption = (props: AutocompleteOptionProps) => {
  const { optionProps, option, inputValue, selected, setSearchInputValue } = props

  // @todo: write datadog e2e test:
  const selectOption: React.MouseEventHandler<HTMLButtonElement> = (e) => {
    e.stopPropagation()
    setSearchInputValue(option)
  }

  return (
    <li {...optionProps} className={clsx(styles.wrapper, selected && styles.selected)}>
      <div className={styles.option}>
        <AutocompleteIcon id={option.id} type={option.type} />
        <AutocompleteContent text={option.title} query={inputValue} />
        <AutocompleteButton type={option.type} onClick={selectOption} />
      </div>
    </li>
  )
}

type AutocompleteIconProps = {
  id?: string
  type?: ESuggestionType
}

const AutocompleteIcon = (props: AutocompleteIconProps) => {
  const { id, type } = props

  if (type === ESuggestionType.Category) {
    return <CategoryIcon catId={`hid${id}`} size="s" imgSize="xxs" />
  }

  if (type === ESuggestionType.History) {
    return <ClockIcon className={styles.icon} />
  }

  if (type === ESuggestionType.Suggestion) {
    return <SearchIcon className={styles.icon} />
  }

  return <SearchIcon className={styles.icon} />
}

type AutocompleteContentProps = {
  text: string
  query: string
}

const AutocompleteContent = (props: AutocompleteContentProps) => {
  const { text, query } = props

  const matches = match(text, query, { insideWords: true })
  const parts = parse(text, matches)

  return (
    <div className={styles.content}>
      {parts.map((part, i) => (
        <AutocompleteContentPart key={i} text={part.text} highlighted={!!query && part.highlight} />
      ))}
    </div>
  )
}

type AutocompleteContentPartProps = {
  text: string
  highlighted: boolean
}

const AutocompleteContentPart = (props: AutocompleteContentPartProps) => {
  const { text, highlighted } = props
  const variant = highlighted ? 'bodyBold' : 'body'

  return (
    <Typography variant={variant} data-track-id="suggestSearchTerm">
      {text}
    </Typography>
  )
}

type AutocompleteButtonProps = {
  type?: ESuggestionType
  onClick?: React.MouseEventHandler<HTMLButtonElement>
}

const AutocompleteButton = (props: AutocompleteButtonProps) => {
  const { type, onClick } = props

  if (type === ESuggestionType.History || type === ESuggestionType.Suggestion) {
    return (
      <IconButton size="small" className={styles.button} onClick={onClick}>
        <LinkExternalIcon />
      </IconButton>
    )
  }

  return null
}
